import { IContextualMenuItem } from '@fluentui/react';
import {
  forwardRef,
  HTMLAttributes,
  Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import { IComponent, IconName } from '../../models/components.models';
import { useClassNames } from '../../utils/hooks';
import { useTheme } from '../../utils/hooks';
import { Button } from '../Button/Button';
import { buttonStylesIcon, buttonStylesIconCard } from '../Button/Button.styles';
import {
  IKeyValuePairClassnames,
  IKeyValuePairStyles,
  keyValuePairStyles,
  moreButtonStyles,
} from './KeyValuePair.styles';
import { KeyValuePairValidationFn } from './KeyValuePairEditor';

export interface IKeyValuePairProps extends IComponent<IKeyValuePairStyles>, HTMLAttributes<any> {
  keyValue: [string, string];
  onEdit?: (key: string, value: string, isNew?: boolean) => void;
  onDelete?: (key: string) => void;
  validation?: KeyValuePairValidationFn;
  allowEmptyValues?: boolean;
  keyLabel?: string;
  valueLabel?: string;
  isNew: boolean;
  onClose?: () => void;
  readOnly?: boolean;
  editOnly?: boolean;
  directlyEditable?: boolean;
}

export enum KeyValuePairSubmitOutcome {
  INVALID = 'invalid',
  SUCCESSFUL = 'successful',
  UNSUCCESSFUL = 'unsuccessful',
}

export const KeyValuePair = forwardRef<HTMLDivElement, IKeyValuePairProps>(
  (props: IKeyValuePairProps, ref: Ref<HTMLDivElement>) => {
    const theme = useTheme();
    const elementRef = useRef<HTMLDivElement | null>(null);
    const keyRef = useRef<HTMLInputElement | null>(null);
    const valueRef = useRef<HTMLTextAreaElement | null>(null);
    useImperativeHandle(
      ref,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      () => {
        return {
          submitKeyValue() {
            // eslint-disable-next-line react-hooks/immutability
            return submitKeyValue();
          },
          ...elementRef.current,
        };
      },
      []
    );
    const {
      keyValue,
      onEdit,
      onDelete,
      className,
      styles,
      validation,
      allowEmptyValues,
      keyLabel,
      valueLabel,
      isNew,
      onClose,
      readOnly,
      editOnly,
      directlyEditable,
      ...elementProps
    } = props;
    const blurKeyHandlerRef = useRef<() => void>();
    const blurValueHandlerRef = useRef<() => void>();
    const [originalValue] = useState<string>(keyValue[1]);
    const [editMode, setEditMode] = useState<boolean>(
      readOnly ? false : directlyEditable || (!keyValue[0] && !keyValue[1])
    );
    const [keyValidationError, setKeyValidationError] = useState<string | undefined>();
    const [valueValidationError, setValueValidationError] = useState<string | undefined>();
    const showRevertButton = useMemo(
      () => Boolean(valueRef.current && valueRef.current.value !== originalValue),
      [originalValue, valueRef.current?.value]
    );
    const copyValue = () => {
      navigator.clipboard.writeText(keyValue[1] || '').catch((err) => {
        // clipboard requires a secure context like `https://` or `localhost` or `127.0.0.1`. if not, it will not allow to use the clipboard.
        console.warn(err.message);
      });
    };
    const validateKey = useCallback((): boolean => {
      const key = keyRef.current?.value;
      if (!key) {
        setKeyValidationError('The key may not be empty.');
        return false;
      }
      const validationMessage = validation ? validation({ key, value: undefined }) : undefined;
      if (!validationMessage) {
        setKeyValidationError(undefined);
        return true;
      } else {
        if (validationMessage.keyValidation) {
          setKeyValidationError(validationMessage.keyValidation);
          return false;
        } else {
          setKeyValidationError(undefined);
          return true;
        }
      }
    }, [keyRef, keyValue, validation]);
    const validateValue = useCallback((): boolean => {
      const value = valueRef.current?.value;
      if (!value && !allowEmptyValues) {
        setValueValidationError('The value may not be empty.');
        return false;
      }
      const validationMessage = validation ? validation({ key: undefined, value }) : undefined;
      if (!validationMessage) {
        setValueValidationError(undefined);
        return true;
      } else {
        if (validationMessage.valueValidation) {
          setValueValidationError(validationMessage.valueValidation);
          return false;
        } else {
          setValueValidationError(undefined);
          return true;
        }
      }
    }, [valueRef, keyValue, validation, allowEmptyValues]);

    const submitKeyValue = useCallback((): KeyValuePairSubmitOutcome => {
      if (!editMode || !onEdit || (isNew && (!keyRef.current || !valueRef.current))) {
        if (!directlyEditable) setEditMode(false);
        return KeyValuePairSubmitOutcome.UNSUCCESSFUL;
      }
      if ((isNew && !validateKey()) || !validateValue()) {
        return KeyValuePairSubmitOutcome.INVALID;
      }
      const key = keyRef.current?.value;
      const value = valueRef.current?.value;
      // we know the key and value are not empty, since we validated this in the validation step of this function.
      if (isNew) {
        onEdit(key!, value!, isNew);
      } else {
        onEdit(keyValue[0], value!, isNew);
      }
      if (!directlyEditable) setEditMode(false);
      setKeyValidationError(undefined);
      setValueValidationError(undefined);
      return KeyValuePairSubmitOutcome.SUCCESSFUL;
    }, [editMode, onEdit, isNew, directlyEditable, keyRef, valueRef, validateKey, validateValue, keyValue]);
    const onCloseEdit = () => {
      if (directlyEditable) return;
      setEditMode(false);
      if (onClose) {
        onClose();
      }
    };
    const classNames = useClassNames<IKeyValuePairStyles, IKeyValuePairClassnames>(
      'KeyValuePairEditor',
      keyValuePairStyles,
      styles,
      className
    );
    const buttonMenuItems: IContextualMenuItem[] = [
      {
        key: 'copy-pair',
        text: 'Copy Value',
        'data-test': `${keyValue[0]}--copy-button`,
        onClick: copyValue,
      },
      {
        key: 'edit-pair',
        text: 'Edit',
        'data-test': `${keyValue[0]}--edit-button`,
        onClick: () => setEditMode(!editMode),
      },
      ...(editOnly
        ? []
        : [
            {
              key: 'delete-pair',
              text: 'Delete',
              'data-test': `${keyValue[0]}--delete-button`,
              onClick: () => {
                if (onDelete) onDelete(keyValue[0]);
              },
              itemProps: {
                styles: {
                  root: {
                    backgroundColor: theme.semanticColors?.buttonDanger,
                    color: theme.palette?.white || 'white',
                    '&.ms-ContextualMenu-link:hover': {
                      backgroundColor: theme.semanticColors?.buttonDangerHovered,
                      color: theme.palette?.white || 'white',
                    },
                  },
                },
              },
            },
          ]),
    ];
    useEffect(() => {
      if (editMode && keyRef.current) {
        keyRef.current?.focus();
      }
    }, [editMode]);
    useEffect(() => {
      blurKeyHandlerRef.current = () => {
        validateKey();
      };
    }, [keyRef, validateKey, directlyEditable]);
    useEffect(() => {
      if (!keyRef?.current) return;

      const listener = () => {
        blurKeyHandlerRef.current?.();
      };

      const el = keyRef.current;
      el.addEventListener('blur', listener);

      return () => {
        el.removeEventListener('blur', listener);
      };
    }, []);
    useEffect(() => {
      blurValueHandlerRef.current = () => {
        if (directlyEditable) {
          submitKeyValue();
        } else {
          validateValue();
        }
      };
    }, [directlyEditable, submitKeyValue, validateValue]);
    useEffect(() => {
      if (!valueRef?.current) return;

      const listener = () => {
        blurValueHandlerRef.current?.();
      };

      const el = valueRef.current;
      el.addEventListener('blur', listener);

      return () => {
        el.removeEventListener('blur', listener);
      };
    }, []);

    return editMode ? (
      <div {...elementProps} className={classNames.row} ref={elementRef}>
        <div className={classNames.keyColumn}>
          {!keyValue[0] ? (
            <>
              <input
                key={`key-${keyValue[0]}`}
                className={`${classNames.input} ${keyValidationError ? classNames.validationInputError : ''}`}
                ref={keyRef}
                type={'text'}
                name={'key'}
                placeholder={keyLabel || 'Key'}
              />
              {keyValidationError && <div className={classNames.validationErrorMessage}>{keyValidationError}</div>}
            </>
          ) : (
            keyValue[0]
          )}
        </div>
        <div className={classNames.valueColumn}>
          <textarea
            key={`value-${keyValue[0]}`}
            className={`${classNames.textArea} ${valueValidationError ? classNames.validationInputError : ''}`}
            ref={valueRef}
            name={'value'}
            placeholder={valueLabel || 'Value'}
            defaultValue={keyValue[1]}
          ></textarea>
          {valueValidationError && <div className={classNames.validationErrorMessage}>{valueValidationError}</div>}
        </div>
        {!readOnly && (
          <div className={classNames.buttonColumn} style={{ width: editOnly ? 35 : 70 }}>
            {directlyEditable ? (
              <>
                <Button
                  styles={[buttonStylesIcon, buttonStylesIconCard]}
                  iconName={'Refresh'}
                  disabled={!showRevertButton}
                  onClick={() => {
                    if (valueRef.current) valueRef.current.value = originalValue;
                    submitKeyValue();
                  }}
                  data-test={`revert-value-button`}
                  style={{
                    opacity: showRevertButton ? '1' : '0.5',
                  }}
                />
                {!editOnly && (
                  <Button
                    styles={[buttonStylesIcon, buttonStylesIconCard]}
                    iconName={'Delete'}
                    onClick={() => {
                      if (onDelete) onDelete(keyValue[0]);
                    }}
                    data-test={`${keyValue[0]}--delete-button`}
                  />
                )}
              </>
            ) : (
              <>
                <Button
                  styles={[buttonStylesIcon, buttonStylesIconCard]}
                  iconName={'CheckMark'}
                  onClick={submitKeyValue}
                  data-test={`submit-new-button`}
                  style={{
                    cursor: keyValidationError || valueValidationError ? 'not-allowed' : 'pointer',
                    opacity: keyValidationError || valueValidationError ? '0.5' : '1',
                  }}
                />
                <Button
                  styles={[buttonStylesIcon, buttonStylesIconCard]}
                  iconName={'Cancel'}
                  onClick={onCloseEdit}
                  data-test={`${keyValue[0]}--cancel-edit-button`}
                />
              </>
            )}
          </div>
        )}
      </div>
    ) : (
      <div className={classNames.row}>
        <div className={classNames.keyColumn}>
          <span title={keyValue[0]}>{keyValue[0]}</span>
        </div>
        <div
          className={classNames.valueColumn}
          onDoubleClick={!readOnly && !directlyEditable ? () => setEditMode(!editMode) : undefined}
        >
          <pre title={keyValue[1]}>{keyValue[1]}</pre>
        </div>
        {!readOnly && (
          <div className={classNames.buttonColumn}>
            <Button
              styles={[buttonStylesIcon, buttonStylesIconCard, moreButtonStyles]}
              menuIconName={IconName.More}
              menuItems={buttonMenuItems}
              data-test={`${keyValue[0]}--more-button`}
            />
          </div>
        )}
      </div>
    );
  }
);
